import type { JSX } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useMe, isUnauthenticated } from './lib/queries';
import { useRealtimeSignals } from './lib/useRealtimeSignals';
import { Desk } from './desk/Desk';
import { windowKeyOf } from './desk/stack';
import { LoginPage } from './auth/LoginPage';
import { ExamLoginPage } from './auth/ExamLoginPage';
import { t } from './messages';

/**
 * Les entrées publiques de connexion (NDRC et BTS blanc), puis le bureau authentifié.
 *
 * Depuis le multi-fenêtres (7 septembre 2026), le tableau des routes de fenêtre n'est plus ici :
 * il est joué une fois **par fenêtre ouverte** (`desk/WindowSlot.tsx`), chacune contre sa propre
 * adresse. Un `Routes` unique n'aurait pu monter qu'une fenêtre à la fois — c'était exactement la
 * limite qu'on lève.
 *
 * L'adresse du navigateur désigne toujours quelque chose de précis : la fenêtre du **premier
 * plan**. Recharger la page rouvre donc celle qui était devant, et « précédent » referme ce qu'on
 * vient d'ouvrir.
 *
 * `/teacher/*` porte l'espace enseignant (`teacher/TeacherSpace.tsx`). Une version antérieure de ce
 * commentaire annonçait qu'aucune route de ce nom ne serait posée « pas même vide » — ce qui était
 * la bonne décision tant qu'elle n'aurait affiché qu'un « bientôt ». Elle porte maintenant quatre
 * outils qui fonctionnent, demandés mot pour mot par l'enseignant le jour où il a découvert qu'il
 * avait « exactement la même interface que les étudiants ».
 *
 * La section est **dans l'URL** comme le reste (`/teacher/presence`, `/teacher/apps`,
 * `/teacher/class`, `/teacher/student/<id>`) : recharger la page revient au même écran, et
 * « précédent » remonte de l'espace d'un élève à la liste de la classe. `/teacher` seul ouvre le
 * suivi de séance — l'écran de la séance en cours, celui qu'on ouvre le plus souvent
 * (`desk/stack.ts`, `normalizePath`).
 *
 * **Aucune de ces routes n'est un contrôle d'accès.** Un élève qui les atteint (adresse tapée à la
 * main) voit une page qui le lui dit ; ce qui REFUSE est le serveur, et lui seul : `role:
 * 'teacher'` sur chaque opération du cœur appelée par ces écrans, `requireSession(..., { role:
 * 'teacher' })` sur les deux routes d'import et de réinitialisation.
 */

/**
 * Porte d'entrée : tant qu'on ne sait pas qui est connecté, on ne rend ni bureau ni dock. Un 401
 * (et lui seul) renvoie à l'écran de connexion ; toute autre panne s'affiche, en français, sans
 * effacer la session.
 */
function DeskLayout(): JSX.Element {
  const me = useMe();
  const location = useLocation();
  // Le fil temps réel, monté UNE fois pour tout l'onglet et au-dessus de tous les écrans : ce que
  // l'enseignant fait (activer un outil, distribuer un fichier, lancer une activité) doit arriver
  // ici sans que l'élève ait à recharger sa page. Posé à ce niveau parce qu'il ne dépend d'aucune
  // classe ni d'aucun écran — il ne prend aucun paramètre, le cœur dérive de la session les canaux
  // que cet onglet a le droit de lire. Voir `lib/useRealtimeSignals.ts`.
  //
  // Avant l'appel conditionnel qui suit (`me.isPending`), comme tout hook : le rendu de secours
  // « Chargement… » ne doit pas couper le fil, sinon un onglet dont la session met du temps à
  // revenir démarrerait puis arrêterait le battement à chaque passage.
  useRealtimeSignals();

  if (me.isPending) {
    return (
      <div className="desk">
        <p className="desk-notice">{t('desk.loading')}</p>
      </div>
    );
  }
  if (me.isError || me.data === undefined) {
    if (isUnauthenticated(me.error)) return <Navigate to="/login" replace />;
    return (
      <div className="desk">
        <p className="desk-notice">
          {me.error instanceof Error ? me.error.message : t('desk.errorTitle')}
        </p>
      </div>
    );
  }
  // Un mot de passe provisoire doit être changé : on ouvre « Mon compte », qui porte le
  // formulaire ET la phrase qui explique pourquoi. C'est le cas de TOUS les élèves importés par
  // l'enseignant à leur première connexion — les laisser devant un bureau muet serait le premier
  // contact de la classe avec la plateforme.
  if (me.data.mustChangePassword && location.pathname !== '/me') {
    return <Navigate to="/me" replace />;
  }
  // Une adresse qui n'ouvre aucune fenêtre (saisie à la main, signet périmé) ramène au bureau nu
  // plutôt que de laisser la barre d'adresse désigner quelque chose qui n'existe pas.
  if (location.pathname !== '/' && windowKeyOf(location.pathname) === null) {
    return <Navigate to="/" replace />;
  }
  return <Desk me={me.data} />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/exam/:examId" element={<ExamLoginPage />} />
      <Route path="*" element={<DeskLayout />} />
    </Routes>
  );
}
